import i18n from 'i18next';
import { reportMissingTranslationKey } from './missingKeyReporter';

export type I18nLocale = Record<string, Record<string, unknown>>;

/**
 * The default (English) i18n locale — an empty record that causes `t()` to
 * return its built-in default text for every key.  Use this instead of a bare
 * `{}` literal so the intent is explicit and grep-able.
 */
export const DEFAULT_ARDOQ_I18NLOCALE: I18nLocale = {};

type Params = Record<string, unknown>;

const getNestedValue = (
  obj: Record<string, unknown>,
  path: string
): string | undefined => {
  // Try flat key first (e.g. "buttons.save" as a literal property name)
  const flat = obj[path];
  if (typeof flat === 'string') return flat;

  // Fall back to nested traversal (e.g. { buttons: { save: "..." } })
  const parts = path.split('.');
  let current: unknown = obj;
  for (const part of parts) {
    if (typeof current !== 'object' || current === null) return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
};

const resolve = (l: I18nLocale, key: string): string | undefined => {
  const colonIdx = key.indexOf(':');
  if (colonIdx === -1) {
    return getNestedValue(
      (l['translation'] ?? {}) as Record<string, unknown>,
      key
    );
  }
  const namespace = key.slice(0, colonIdx);
  const path = key.slice(colonIdx + 1);
  return getNestedValue((l[namespace] ?? {}) as Record<string, unknown>, path);
};

/**
 * Resolve the CLDR plural form (`'zero' | 'one' | 'two' | 'few' | 'many' |
 * 'other'`) for a given count and language. Returns `undefined` if the
 * language is unknown/empty or `Intl.PluralRules` throws on a malformed tag,
 * so callers fall back to flat-key lookup safely.
 */
const resolvePluralForm = (
  language: string,
  count: number
): Intl.LDMLPluralRule | undefined => {
  if (!language) return undefined;
  try {
    return new Intl.PluralRules(language).select(count);
  } catch {
    return undefined;
  }
};

const resolveWithPlural = (
  l: I18nLocale,
  key: string,
  params: Params | undefined
): string | undefined => {
  if (params && typeof params['count'] === 'number') {
    const pluralForm = resolvePluralForm(i18n.language, params['count']);
    if (pluralForm) {
      const pluralized = resolve(l, `${key}_${pluralForm}`);
      if (pluralized !== undefined) return pluralized;
    }
  }
  return resolve(l, key);
};

const interpolate = (text: string, params: Params): string =>
  text.replace(/\{\{(\w+)\}\}/g, (match, k: string) =>
    k in params ? String(params[k] ?? '') : match
  );

/**
 * Pure translation function. Import statically and pass `locale` as the first argument.
 *
 * ```ts
 * import { t, type I18nLocale } from '@ardoq/i18n';
 *
 * // Simple form
 * const MyComponent = ({ locale }: { locale: I18nLocale }) => (
 *   <div>{t(locale, 'ns:key', 'Default text')}</div>
 * );
 *
 * // With interpolation params
 * t(locale, 'ns:key', { name: 'Alice' }, 'Hello, {{name}}!')
 * ```
 *
 * Keys are namespace-prefixed: `'namespace:path.to.key'`.
 * Falls back to `defaultText` when the key is not found (e.g. English locale).
 */
export function t(l: I18nLocale, key: string, defaultText?: string): string;
export function t(
  l: I18nLocale,
  key: string,
  params: Params,
  defaultText?: string
): string;
export function t(
  l: I18nLocale,
  key: string,
  defaultTextOrParams?: string | Params,
  defaultText?: string
): string {
  const params =
    typeof defaultTextOrParams === 'object' ? defaultTextOrParams : undefined;
  const fallback =
    typeof defaultTextOrParams === 'string'
      ? defaultTextOrParams
      : (defaultText ?? '');

  const resolved = resolveWithPlural(l, key, params);
  if (resolved === undefined) {
    const activeLanguage = i18n.language;
    // Skip the reporter when the bundle hasn't loaded yet — closes the race
    // window between view mount and the setTranslationBundle reduce that
    // otherwise floods Sentry with false-positive "missing key" events.
    const bundleLoaded = Object.keys(l).length > 0;
    if (bundleLoaded && activeLanguage && !activeLanguage.startsWith('en')) {
      const colonIdx = key.indexOf(':');
      const namespace =
        colonIdx === -1 ? 'translation' : key.slice(0, colonIdx);
      const lookupKey = colonIdx === -1 ? key : key.slice(colonIdx + 1);
      reportMissingTranslationKey({
        locale: activeLanguage,
        namespace,
        key: lookupKey,
      });
    }
  }
  const raw = resolved ?? fallback;
  return params ? interpolate(raw, params) : raw;
}
